import { ClientField } from '../types/ClientField';
import { ContactListFilterItem } from '../types/ContactListFilterItem';
import { ConversationGridFiltersItem } from '../types/ConversationGridFiltersItem';
import { IErrorFormValidation } from '../utils/formValidationHelpers';
import { hasDistinctFieldId, hasMatchingFieldId } from '../utils/functionalFilters';
import { mapFieldToGridFilter, mapQueryFilterToGridFilter } from '../utils/functionalMappers';

type ConversationGridFiltersState = {
    fields: ClientField[];
    filters: ConversationGridFiltersItem[];
    formError: IErrorFormValidation | null;
};

export type ConversationGridFiltersActions =
    | {
          type: 'SET_FILTERABLE_FIELDS';
          fields: ClientField[];
      }
    | {
          type: 'SET_FILTERS_FROM_STATE';
          filters: ContactListFilterItem[];
      }
    | {
          type: 'ADD_FORM_FILTER';
          fieldId: number;
      }
    | {
          type: 'MODIFY_FORM_FILTER';
          fieldId: number;
          condition: Partial<
              Pick<ConversationGridFiltersItem, 'operand' | 'value' | 'value2' | 'values'>
          >;
      }
    | {
          type: 'REMOVE_FORM_FILTER';
          fieldId: number;
      }
    | {
          type: 'CLEAR_FORM_FILTERS';
      }
    | {
          type: 'SET_FORM_ERROR';
          formError: IErrorFormValidation | null;
      };

export type ConversationGridFiltersReducer = (
    state: ConversationGridFiltersState,
    action: ConversationGridFiltersActions,
) => ConversationGridFiltersState;

export function conversationGridFiltersReducer(
    state: ConversationGridFiltersState,
    action: ConversationGridFiltersActions,
): ConversationGridFiltersState {
    switch (action.type) {
        case 'SET_FILTERABLE_FIELDS':
            return {
                ...state,
                ...{
                    fields: action.fields.filter(itm => itm.isFilterable),
                },
            };
        case 'SET_FILTERS_FROM_STATE':
            return {
                ...state,
                ...{
                    filters: action.filters.reduce<ConversationGridFiltersItem[]>(
                        (acc, itm) => [
                            ...acc,
                            ...state.fields
                                .filter(hasMatchingFieldId(itm.fieldId))
                                .map(mapQueryFilterToGridFilter(itm)),
                        ],
                        [],
                    ),
                },
            };
        case 'ADD_FORM_FILTER':
            return {
                ...state,
                ...{
                    filters: [
                        ...state.filters,
                        ...state.fields
                            .filter(hasMatchingFieldId(action.fieldId))
                            .map(mapFieldToGridFilter),
                    ],
                },
            };
        case 'MODIFY_FORM_FILTER':
            return {
                ...state,
                ...{
                    filters: state.filters.map(fld =>
                        fld.fieldId === action.fieldId
                            ? {
                                  ...fld,
                                  ...action.condition,
                              }
                            : fld,
                    ),
                },
            };
        case 'REMOVE_FORM_FILTER':
            return {
                ...state,
                ...{
                    filters: state.filters.filter(hasDistinctFieldId(action.fieldId)),
                    formError:
                        state.formError && state.formError.fieldId === action.fieldId
                            ? null
                            : state.formError,
                },
            };
        case 'CLEAR_FORM_FILTERS':
            return {
                ...state,
                ...{
                    filters: [],
                },
            };
        case 'SET_FORM_ERROR':
            return {
                ...state,
                ...{
                    formError: action.formError,
                },
            };
        default:
            return state;
    }
}
